import React, {useEffect, useMemo, useState} from "react";
import {Config} from "../MerchantConfigurationsListComponent/MerchantConfigurationsListComponent";
import styles from './ConfigComponent.module.css';
import {arrayToCsv, csvToArray} from "../../utils/CSV/CSVUtil";


type ConfigComponentProps = {
    config: Config,
    handleModeChange: (newMode: string) => void,
    currentValue: string
};

const ConfigComponent: React.FC<ConfigComponentProps> = ({
                                                             config,
                                                             handleModeChange,
                                                             currentValue,
                                                         }) => {
    const [currentState, setCurrentState] = useState<string>(currentValue);
    const [pendingToAdd, setPendingToAdd] = useState<string>("");

    useEffect(() => {
        setCurrentState(currentValue);
    }, [currentValue]);

    const handleChange = (newValue: string) => {
        handleModeChange(newValue);
        setCurrentState(newValue);
    };

    const selectedValues = useMemo(() => csvToArray(currentState), [currentState]);

    const remainingOptions = useMemo(
        () => config.values.filter(v => !selectedValues.includes(v.value)),
        [config.values, selectedValues]
    );

    useEffect(() => {
        if (!pendingToAdd || !remainingOptions.some(o => o.value === pendingToAdd)) {
            setPendingToAdd(remainingOptions[0]?.value ?? "");
        }
    }, [remainingOptions, pendingToAdd]);

    const addPending = () => {
        if (!pendingToAdd) return;
        if (selectedValues.includes(pendingToAdd)) return;
        const next = [...selectedValues, pendingToAdd];
        handleChange(arrayToCsv(next));
    };

    const removeValue = (val: string) => {
        const next = selectedValues.filter(v => v !== val);
        handleChange(arrayToCsv(next));
    };

    const clearAll = () => handleChange("");


    return (
        <div className={`${styles.configContainer} ${config.configName}`}>
            <label htmlFor={config.configName} className={styles.label}>
                {config.label}:
            </label>

            {config.inputType === 'select' && (
                <select
                    id={config.configName}
                    value={currentState}
                    onChange={(e) => handleChange(e.target.value)}
                    className={styles.select}
                >
                    {config.values.map(({value, label}) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            )}

            {config.inputType === 'radio' && (
                <div className={styles.radioContainer}>
                    {config.values.map(({value, label}) => (
                        <div key={value} className={styles.radioOption}>
                            <input
                                type="radio"
                                id={`${config.configName}-${value}`}
                                name={config.configName}
                                value={value}
                                checked={currentState === value}
                                onChange={(e) => handleChange(e.target.value)}
                            />
                            <label htmlFor={`${config.configName}-${value}`}>{label}</label>
                        </div>
                    ))}
                </div>
            )}

            {config.inputType === 'multiselect' && (
                <div className={styles.multiSelectWrapper}>
                    <div className={styles.multiSelectControls}>
                        <select
                            id={config.configName}
                            value={pendingToAdd}
                            onChange={(e) => setPendingToAdd(e.target.value)}
                            className={styles.multiSelectDropdown}
                            disabled={remainingOptions.length === 0}
                        >
                            {remainingOptions.length === 0 && (
                                <option value="">— All selected —</option>
                            )}
                            {remainingOptions.map(({value, label}) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="btn btn-primary-light"
                            onClick={addPending}
                            disabled={!pendingToAdd}
                        >
                            Add
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary-light"
                            onClick={clearAll}
                        >
                            Clear
                        </button>
                    </div>

                    <div className={styles.selectedValues}>
                        {selectedValues.length === 0 && (
                            <span className={styles.noSelected}>No currencies selected</span>
                        )}
                        {selectedValues.map((val) => {
                            const label =
                                config.values.find((v) => v.value === val)?.label ?? val;
                            return (
                                <span key={val} className={styles.selectedChip} title={val}>
                {label}
                                    <button
                                        type="button"
                                        aria-label={`Remove ${val}`}
                                        onClick={() => removeValue(val)}
                                        className={styles.removeBtn}
                                    >
                  ×
                </button>
              </span>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ConfigComponent;
